import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from "../../../../components/FormElements/TextField";
import CustomDatePicker from "../../../../components/CustomDatePicker/CustomDatePicker";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import ButtonOutlined from "../../../../components/FormElements/ButtonOutlined";
import offersActions from "../../../../store/offersStore/offersActions";
import ConsentModal from "../ConsentModal";
import styles from './offersForm.module.scss';

const OFFER_TYPES = [
  { value: 'percentage', label: 'Процент' },
  { value: 'fixed_amount', label: 'Фиксированная сумма' },
  { value: 'buy_x_get_y', label: 'Купи X получи Y' },
  { value: 'gift', label: 'Подарок' }
];

const CATEGORIES = [
  { value: 'food', label: 'Еда' },
  { value: 'beauty', label: 'Красота' },
  { value: 'fashion', label: 'Мода' },
  { value: 'electronics', label: 'Электроника' },
  { value: 'health', label: 'Здоровье' },
  { value: 'entertainment', label: 'Развлечения' },
  { value: 'travel', label: 'Путешествия' },
  { value: 'education', label: 'Образование' },
  { value: 'sports', label: 'Спорт' },
  { value: 'home_and_garden', label: 'Дом и сад' }
];

const OffersForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { info } = useSelector((s) => s.branchReducer);
  const { pending, currentOffer, communities, communitiesPending } = useSelector((s) => s.offersReducer);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);

  useEffect(() => {
    if (id && info && info.id) {
      dispatch(offersActions.getOfferDetails(info.id, id));
    }
    return () => {
      dispatch(offersActions.clearCurrentOffer());
      setExistingImages([]);
      setImages([]);
    };
  }, [id, info?.id, dispatch]);

  // Set existing images when currentOffer is loaded
  useEffect(() => {
    if (currentOffer && currentOffer.images) {
      setExistingImages(currentOffer.images);
    }
  }, [currentOffer]);

  useEffect(() => {
    // Fetch communities only once on mount
    dispatch(offersActions.getCommunities({ limit: 100, offset: 0 }));
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string().required(t('NSOffers.titleRequired') || 'Название обязательно'),
    description: Yup.string(),
    offer_type: Yup.string().required(t('NSOffers.typeRequired') || 'Тип обязателен'),
    offer_value: Yup.number().min(0.01, t('NSOffers.valuePositive') || 'Значение должно быть положительным'),
    promocode_required: Yup.boolean(),
    promocode: Yup.string().when('promocode_required', {
      is: true,
      then: (schema) => schema.required(t('NSOffers.promocodeRequired') || 'Промокод обязателен'),
      otherwise: (schema) => schema.nullable()
    }),
    min_purchase_amount: Yup.number().min(0).nullable(),
    max_offer_amount: Yup.number().min(0).nullable(),
    start_date: Yup.date().required(t('NSOffers.startDateRequired') || 'Дата начала обязательна'),
    end_date: Yup.date().required(t('NSOffers.endDateRequired') || 'Дата окончания обязательна'),
    is_exclusive: Yup.boolean(),
    communities: Yup.array().of(Yup.string()),
    categories: Yup.array().of(Yup.string())
  });

  // Memoize initial values to prevent infinite re-renders with enableReinitialize
  const initialValues = useMemo(() => ({
    title: currentOffer?.title || '',
    description: currentOffer?.description || '',
    offer_type: currentOffer?.offer_type || 'percentage',
    offer_value: currentOffer?.offer_value || '',
    promocode_required: currentOffer?.promocode_required || false,
    promocode: currentOffer?.promocode || '',
    min_purchase_amount: currentOffer?.min_purchase_amount || '',
    max_offer_amount: currentOffer?.max_offer_amount || '',
    start_date: currentOffer?.start_date ? new Date(currentOffer.start_date) : new Date(),
    end_date: currentOffer?.end_date ? new Date(currentOffer.end_date) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    is_exclusive: currentOffer?.is_exclusive || false,
    communities: currentOffer?.communities || [],
    categories: currentOffer?.categories || []
  }), [currentOffer]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      // Create FormData for multipart/form-data
      const formData = new FormData();

      formData.append('title', values.title);
      formData.append('description', values.description || '');
      formData.append('offer_type', values.offer_type);
      formData.append('offer_value', ['buy_x_get_y', 'gift'].includes(values.offer_type) ? 1 : parseFloat(values.offer_value));
      formData.append('promocode_required', values.promocode_required);
      if (values.promocode_required && values.promocode) {
        formData.append('promocode', values.promocode);
      }
      if (values.min_purchase_amount) {
        formData.append('min_purchase_amount', parseFloat(values.min_purchase_amount));
      }
      if (values.max_offer_amount) {
        formData.append('max_offer_amount', parseFloat(values.max_offer_amount));
      }
      formData.append('start_date', values.start_date.toISOString());
      formData.append('end_date', values.end_date.toISOString());
      formData.append('is_exclusive', values.is_exclusive);

      // Append arrays
      if (values.communities && values.communities.length > 0) {
        values.communities.forEach(communityId => {
          formData.append('communities', communityId);
        });
      }
      if (values.categories && values.categories.length > 0) {
        values.categories.forEach(category => {
          formData.append('categories', category);
        });
      }

      // Append images
      images.forEach((image) => {
        formData.append('images', image);
      });

      if (id) {
        dispatch(offersActions.updateOffer(info.id, id, formData, () => {
          history.push('/offers');
        }));
      } else {
        dispatch(offersActions.createOffer(info.id, formData, () => {
          history.push('/offers');
        }));
      }
    }
  });

  const shouldShowOfferValue = !['buy_x_get_y', 'gift'].includes(formik.values.offer_type);

  const handleCategoryToggle = (categoryValue) => {
    const currentCategories = formik.values.categories || [];
    if (currentCategories.includes(categoryValue)) {
      formik.setFieldValue('categories', currentCategories.filter(c => c !== categoryValue));
    } else {
      formik.setFieldValue('categories', [...currentCategories, categoryValue]);
    }
  };

  const handleCommunityToggle = (communityId) => {
    const currentCommunities = formik.values.communities || [];
    if (currentCommunities.includes(communityId)) {
      formik.setFieldValue('communities', currentCommunities.filter(c => c !== communityId));
    } else {
      formik.setFieldValue('communities', [...currentCommunities, communityId]);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    setImages(prevImages => [...prevImages, ...files]);
  };

  const handleRemoveImage = (index) => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.container}>
      <h1>{id ? (t('NSOffers.editOffer') || 'Редактировать акцию') : (t('NSOffers.createOffer') || 'Создать акцию')}</h1>
      <form className={styles.form} onSubmit={formik.handleSubmit}>
        <div className={styles.formContent}>
          <div className={styles.column}>
            {/* Title */}
            <div className={styles.block}>
              <label><b>{t('title') || 'Название'}<span>*</span></b></label>
              <TextField
                placeholder={t('NSOffers.titlePlaceholder') || 'Название акции'}
                width="100%"
                value={formik.values.title}
                error={formik.touched.title && formik.errors.title}
                onChange={(e) => formik.setFieldValue('title', e.target.value)}
                required
              />
            </div>

            {/* Description */}
            <div className={styles.block}>
              <label><b>{t('description') || 'Описание'}</b></label>
              <textarea
                className={styles.textarea}
                placeholder={t('NSOffers.descriptionPlaceholder') || 'Описание акции'}
                value={formik.values.description}
                onChange={(e) => formik.setFieldValue('description', e.target.value)}
                rows={4}
              />
            </div>

            {/* Offer Type */}
            <div className={styles.block}>
              <label><b>{t('NSOffers.type') || 'Тип акции'}<span>*</span></b></label>
              <select
                className={styles.select}
                value={formik.values.offer_type}
                onChange={(e) => formik.setFieldValue('offer_type', e.target.value)}
              >
                {OFFER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Offer Value */}
            {shouldShowOfferValue && (
              <div className={styles.block}>
                <label><b>{t('NSOffers.value') || 'Значение'}<span>*</span></b></label>
                <TextField
                  placeholder={formik.values.offer_type === 'percentage' ? '10' : '1000'}
                  width="180px"
                  value={formik.values.offer_value}
                  error={formik.touched.offer_value && formik.errors.offer_value}
                  onChange={(e) => formik.setFieldValue('offer_value', e.target.value)}
                  required
                />
                <p className={styles.blockText}>
                  {formik.values.offer_type === 'percentage'
                    ? (t('NSOffers.percentageHint') || 'Процент скидки (например, 10 для 10%)')
                    : (t('NSOffers.fixedAmountHint') || 'Сумма скидки в тенге')}
                </p>
              </div>
            )}

            {/* Min Purchase Amount */}
            <div className={styles.block}>
              <label><b>{t('NSOffers.minPurchase') || 'Минимальная сумма покупки'}</b></label>
              <TextField
                placeholder="0"
                width="180px"
                value={formik.values.min_purchase_amount}
                onChange={(e) => formik.setFieldValue('min_purchase_amount', e.target.value)}
              />
            </div>

            {/* Max Offer Amount */}
            <div className={styles.block}>
              <label><b>{t('NSOffers.maxOfferAmount') || 'Максимальная сумма акции'}</b></label>
              <TextField
                placeholder="0"
                width="180px"
                value={formik.values.max_offer_amount}
                onChange={(e) => formik.setFieldValue('max_offer_amount', e.target.value)}
              />
            </div>
          </div>

          <div className={styles.column}>
            {/* Period */}
            <div className={styles.block}>
              <label><b>{t('NSAds.period') || 'Период'}<span>*</span></b></label>
              <CustomDatePicker
                trans={true}
                tStartDate={formik.values.start_date}
                tEndDate={formik.values.end_date}
                tSetStartDate={(v) => formik.setFieldValue('start_date', v)}
                tSetEndDate={(v) => formik.setFieldValue('end_date', v)}
              />
            </div>

            {/* Promocode Required */}
            <div className={styles.block}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formik.values.promocode_required}
                  onChange={(e) => formik.setFieldValue('promocode_required', e.target.checked)}
                />
                <b>{t('NSOffers.promocodeRequired') || 'Требуется промокод'}</b>
              </label>
            </div>

            {/* Promocode */}
            {formik.values.promocode_required && (
              <div className={styles.block}>
                <label><b>{t('NSOffers.promocode') || 'Промокод'}<span>*</span></b></label>
                <TextField
                  placeholder="PROMO2024"
                  width="180px"
                  value={formik.values.promocode}
                  error={formik.touched.promocode && formik.errors.promocode}
                  onChange={(e) => formik.setFieldValue('promocode', e.target.value.toUpperCase())}
                  required
                />
              </div>
            )}

            {/* Is Exclusive */}
            <div className={styles.block}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formik.values.is_exclusive}
                  onChange={(e) => formik.setFieldValue('is_exclusive', e.target.checked)}
                />
                <b>{t('NSOffers.isExclusive') || 'Эксклюзивная акция'}</b>
              </label>
            </div>

            {/* Communities */}
            {formik.values.is_exclusive && (
              <div className={styles.block}>
                <label><b>{t('NSOffers.communities') || 'Сообщества'}</b></label>
                <div className={styles.communitiesList}>
                  {communitiesPending ? (
                    <p>Загрузка...</p>
                  ) : communities && communities.length > 0 ? (
                    communities.map((community) => (
                      <label key={community.id} className={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          checked={(formik.values.communities || []).includes(community.id)}
                          onChange={() => handleCommunityToggle(community.id)}
                        />
                        <span>{community.name}</span>
                      </label>
                    ))
                  ) : (
                    <p>{t('NSOffers.noCommunities') || 'Нет доступных сообществ'}</p>
                  )}
                </div>
              </div>
            )}

            {/* Categories */}
            <div className={styles.block}>
              <label><b>{t('NSOffers.categories') || 'Категории'}</b></label>
              <div className={styles.categoriesList}>
                {CATEGORIES.map((category) => (
                  <label key={category.value} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={(formik.values.categories || []).includes(category.value)}
                      onChange={() => handleCategoryToggle(category.value)}
                    />
                    <span>{category.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Image Upload */}
            <div className={styles.block}>
              <label><b>Изображения</b></label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className={styles.fileInput}
              />
              {(existingImages.length > 0 || images.length > 0) && (
                <div className={styles.imagePreview}>
                  {/* Existing images from server */}
                  {existingImages.map((image, index) => (
                    <div key={`existing-${index}`} className={styles.imageItem}>
                      <img
                        src={image.url}
                        alt={`Existing ${index + 1}`}
                        className={styles.previewImage}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(index)}
                        className={styles.removeImageBtn}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {/* New images to upload */}
                  {images.map((image, index) => (
                    <div key={`new-${index}`} className={styles.imageItem}>
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className={styles.previewImage}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className={styles.removeImageBtn}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Consent Checkbox - only show when creating new offer */}
        {!id && (
          <div className={styles.consentBlock}>
            <label className={styles.consentLabel}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className={styles.consentCheckbox}
              />
              <span className={styles.consentText}>
                Я согласен с{' '}
                <span
                  className={styles.consentLink}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsConsentModalOpen(true);
                  }}
                >
                  правилами создания акции
                </span>
                <span className={styles.required}>*</span>
              </span>
            </label>
          </div>
        )}

        <div className={styles.formFooter}>
          <ButtonFilled
            type="submit"
            loading={pending}
            disabled={!id && !consentChecked}
          >
            {t('save') || 'Сохранить'}
          </ButtonFilled>
          <ButtonOutlined variant="green" color="black" to="/offers">
            {t('cancel') || 'Отмена'}
          </ButtonOutlined>
        </div>
      </form>

      {/* Consent Modal */}
      <ConsentModal
        isOpen={isConsentModalOpen}
        onClose={() => setIsConsentModalOpen(false)}
      />
    </div>
  );
};

export default OffersForm;
